// COP Copilot runtime — the Backend seam (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED VERBATIM from takbot services/agent-runtime/src/backends/types.ts (import source
// changed: @takbot/protocol → ../types.ts). A Backend is the swappable "brain": given the full
// SessionState (the user request, the MCP manifest, the transcript of prior tool calls +
// results), it returns the next PLAN — more tool calls, or the final cited answer.
//
// TWO implementations:
//   - MockBackend   (mock.ts):   deterministic, zero external deps, the DEFAULT/fallback.
//   - OpenAIBackend (openai.ts): TDFBot's preserved /v1 chat-completions path (→ local Qwen3.6).
//
// The loop (loop.ts) is backend-agnostic: it only ever calls plan()/finalize().

import type {
  ContextWindowConfig,
  FinalAnswer,
  McpManifest,
  RuntimeBackend,
  ToolCall,
  ToolResult,
} from "../types.ts";

/** One turn-of-conversation record in the transcript. A `model` row carries the tool calls the
 *  backend chose; a `tool` row carries the results brought back after running them (over the
 *  confined working set). The pairing (by call_id) is what assembleContext folds into [n] blocks. */
export interface TranscriptEntry {
  role: "model" | "tool";
  toolCalls?: ToolCall[];
  toolResults?: ToolResult[];
}

/** Everything a Backend needs to decide the next step. Owned per (session_id, turn_id) by the
 *  loop and mutated as the turn progresses. */
export interface SessionState {
  session_id: string;
  turn_id: string;
  userText: string;
  manifest: McpManifest;
  transcript: TranscriptEntry[];
  config: ContextWindowConfig;
}

/** A backend's decision: run these tool calls next, or finish with this answer. */
export type Plan =
  | { kind: "tool_calls"; tool_calls: ToolCall[] }
  | { kind: "final"; final: FinalAnswer };

/** The swappable LLM/orchestration brain. */
export interface Backend {
  name: RuntimeBackend;
  model: string;
  baseUrl?: string;
  /** Decide the next step from the current session state. */
  plan(state: SessionState): Promise<Plan>;
  /** FORCE an answer NOW from the tool results already gathered — used by the loop when it must
   *  terminate. Always returns a FinalAnswer (synthesized from what it has, never a canned hole). */
  finalize(state: SessionState): Promise<FinalAnswer>;
}

/** Flatten a transcript into the ordered (call, result) pairs the context assembler wants. */
export function pairSteps(
  transcript: TranscriptEntry[],
): Array<{ call: ToolCall; result: ToolResult }> {
  const calls: ToolCall[] = [];
  const resultsById = new Map<string, ToolResult>();
  for (const entry of transcript) {
    if (entry.role === "model" && entry.toolCalls) {
      calls.push(...entry.toolCalls);
    }
    if (entry.role === "tool" && entry.toolResults) {
      for (const r of entry.toolResults) resultsById.set(r.call_id, r);
    }
  }
  const pairs: Array<{ call: ToolCall; result: ToolResult }> = [];
  for (const call of calls) {
    const result = resultsById.get(call.call_id);
    if (result) pairs.push({ call, result });
  }
  return pairs;
}

/** True once every issued tool call has a matching result in the transcript. */
export function allCallsResolved(transcript: TranscriptEntry[]): boolean {
  const issued = new Set<string>();
  const resolved = new Set<string>();
  for (const entry of transcript) {
    if (entry.role === "model" && entry.toolCalls) {
      for (const c of entry.toolCalls) issued.add(c.call_id);
    }
    if (entry.role === "tool" && entry.toolResults) {
      for (const r of entry.toolResults) resolved.add(r.call_id);
    }
  }
  for (const id of issued) if (!resolved.has(id)) return false;
  return true;
}
